import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Baby, ArrowLeft, Shield, Lock, Eye, Database, Users, Globe, Mail } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-policy-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-primary/5 to-background pt-8 pb-12">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 mb-6 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <Baby className="w-7 h-7 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-2xl">CareKids WI</span>
          </div>
          
          <h1 className="font-heading font-bold text-3xl md:text-4xl mb-2">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground">
            Last Updated: December 31, 2025
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        <Card className="card-clay">
          <CardContent className="p-8 prose prose-gray max-w-none">
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-blue-800 mb-1">Our Commitment to Privacy</p>
                  <p className="text-blue-700 text-sm mb-0">
                    At CareKids WI, protecting the privacy of children and families is our top priority. 
                    We are committed to transparency about how we collect, use, and safeguard your information.
                  </p>
                </div>
              </div>
            </div>

            <p className="lead text-lg">
              This Privacy Policy describes how <strong>CareKids WI</strong> (&ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;) 
              collects, uses, and shares information when you use our website, mobile application, and related services 
              (collectively, the &ldquo;Services&rdquo;).
            </p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">1</span>
              Information We Collect
            </h2>
            
            <h3 className="text-lg font-semibold mt-4">Information You Provide</h3>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, phone number, and password when you create an account</li>
              <li><strong>Child Information:</strong> Child&apos;s name, date of birth, medical information, allergies, emergency contacts, and authorized pickup persons</li>
              <li><strong>Staff Information:</strong> Employee details, certifications, background check information, and qualifications</li>
              <li><strong>Facility Information:</strong> Child care center name, address, licensing information, and operational details</li>
              <li><strong>Payment Information:</strong> Billing address and payment method details (processed securely by our payment providers)</li>
              <li><strong>Communications:</strong> Messages, photos, and documents shared through the platform</li>
            </ul>

            <h3 className="text-lg font-semibold mt-4">Information Collected Automatically</h3>
            <ul>
              <li><strong>Usage Data:</strong> How you interact with our Services, including features used and time spent</li>
              <li><strong>Device Information:</strong> Device type, operating system, browser type, and unique device identifiers</li>
              <li><strong>Location Data:</strong> GPS coordinates when using location-based features (such as staff clock-in/clock-out)</li>
              <li><strong>Log Data:</strong> IP address, access times, and pages viewed</li>
            </ul>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">2</span>
              How We Use Your Information
            </h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, maintain, and improve our Services</li>
              <li>Process transactions and send related information</li>
              <li>Send notifications about attendance, activities, and important updates</li>
              <li>Facilitate communication between child care providers and families</li>
              <li>Generate reports and analytics for child care management</li>
              <li>Ensure compliance with Wisconsin DCF regulations</li>
              <li>Respond to your comments, questions, and support requests</li>
              <li>Detect, investigate, and prevent fraudulent or unauthorized activities</li>
              <li>Comply with legal obligations</li>
            </ul>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">3</span>
              Children&apos;s Privacy
            </h2>
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg mb-4">
              <div className="flex items-start gap-2">
                <Users className="w-5 h-5 text-green-600 mt-0.5" />
                <span><strong>Special Protections for Children&apos;s Data</strong></span>
              </div>
            </div>
            <p>
              CareKids WI is designed for use by child care providers and parents/guardians. 
              We take special care to protect information about children:
            </p>
            <ul>
              <li>We collect children&apos;s information only as necessary to provide child care management services</li>
              <li>Children&apos;s information is only accessible to authorized users (parents/guardians and their designated child care providers)</li>
              <li>We do not use children&apos;s information for marketing purposes</li>
              <li>We do not sell children&apos;s personal information under any circumstances</li>
              <li>Parents/guardians may request access to, correction of, or deletion of their child&apos;s information</li>
            </ul>
            <p>
              Child care providers using our platform are responsible for obtaining appropriate parental consent 
              for the collection and use of children&apos;s information as required by applicable laws.
            </p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">4</span>
              Information Sharing
            </h2>
            <p>We may share your information in the following circumstances:</p>
            <ul>
              <li><strong>With Child Care Providers:</strong> Parents&apos; and children&apos;s information is shared with the child care centers where children are enrolled</li>
              <li><strong>With Parents/Guardians:</strong> Information about their children&apos;s activities, attendance, and care</li>
              <li><strong>Service Providers:</strong> Third-party vendors who help us operate our Services (e.g., hosting, payment processing, email delivery)</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority</li>
              <li><strong>Safety:</strong> To protect the rights, property, or safety of CareKids WI, our users, or others</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
            <p className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
              <strong>We do not sell personal information.</strong> We do not share personal information with third parties for their direct marketing purposes.
            </p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">5</span>
              Data Security
            </h2>
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
              <div className="flex items-start gap-2">
                <Lock className="w-5 h-5 text-blue-600 mt-0.5" />
                <span><strong>We take data security seriously.</strong></span>
              </div>
            </div>
            <p>We implement appropriate technical and organizational measures to protect your information, including:</p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication and access controls</li>
              <li>Regular security assessments and monitoring</li>
              <li>Employee training on data protection practices</li>
              <li>Incident response procedures</li>
            </ul>
            <p>
              While we strive to protect your information, no method of transmission over the Internet or electronic 
              storage is 100% secure. We cannot guarantee absolute security.
            </p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">6</span>
              Data Retention
            </h2>
            <p>
              We retain your information for as long as your account is active or as needed to provide you Services. 
              We may also retain and use information as necessary to:
            </p>
            <ul>
              <li>Comply with legal obligations (including Wisconsin DCF record-keeping requirements)</li>
              <li>Resolve disputes</li>
              <li>Enforce our agreements</li>
            </ul>
            <p>
              Child care records may be retained for the period required by Wisconsin Department of Children and Families 
              regulations, even after a child is no longer enrolled.
            </p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">7</span>
              Your Rights and Choices
            </h2>
            <p>You have the following rights regarding your information:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal retention requirements)</li>
              <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
              <li><strong>Location Services:</strong> Disable location tracking through your device settings</li>
            </ul>
            <p>
              To exercise these rights, please contact us using the information provided below. 
              We will respond to your request within 30 days.
            </p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">8</span>
              Cookies and Tracking Technologies
            </h2>
            <p>We use cookies and similar technologies to:</p>
            <ul>
              <li>Keep you logged in to your account</li>
              <li>Remember your preferences</li>
              <li>Understand how you use our Services</li>
              <li>Improve our Services</li>
            </ul>
            <p>
              You can control cookies through your browser settings. Note that disabling cookies may affect 
              the functionality of our Services.
            </p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">9</span>
              Third-Party Services
            </h2>
            <p>
              Our Services may contain links to third-party websites or integrate with third-party services. 
              This Privacy Policy does not apply to those third parties. We encourage you to review the 
              privacy policies of any third-party services you access.
            </p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">10</span>
              Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by:
            </p>
            <ul>
              <li>Posting the new Privacy Policy on this page</li>
              <li>Updating the &ldquo;Last Updated&rdquo; date</li>
              <li>Sending you an email notification (for significant changes)</li>
            </ul>
            <p>
              Your continued use of our Services after any changes indicates your acceptance of the updated Privacy Policy.
            </p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">11</span>
              Contact Us
            </h2>
            <p>If you have questions about this Privacy Policy or our privacy practices, please contact us:</p>
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="mb-2">
                <strong>Email:</strong>{' '}
                <a href="mailto:info@carekidswi.com" className="text-primary hover:underline">
                  info@carekidswi.com
                </a>
              </p>
              <p className="mb-2">
                <strong>General Inquiries:</strong>{' '}
                <a href="mailto:info@carekidswi.com" className="text-primary hover:underline">
                  info@carekidswi.com
                </a>
              </p>
              <p className="mb-0">
                <strong>Platform Name:</strong> CareKids WI
              </p>
            </div>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">12</span>
              Wisconsin-Specific Information
            </h2>
            <p>
              CareKids WI is designed to help child care providers comply with Wisconsin Department of Children 
              and Families (DCF) regulations. Our data collection and retention practices are aligned with 
              DCF requirements for child care licensing and record-keeping.
            </p>
            <p>
              If you have concerns about how a specific child care provider is handling your information, 
              you may contact the Wisconsin DCF directly.
            </p>
          </CardContent>
        </Card>

        {/* Footer Actions */}
        <div className="mt-8 flex justify-center gap-4">
          <Link to="/terms">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Terms and Conditions
            </Button>
          </Link>
          <Link to="/">
            <Button>
              <Baby className="w-4 h-4 mr-2" />
              Go to Home
            </Button>
          </Link>
        </div>

        {/* Additional Links */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>
            By using CareKids WI, you agree to our{' '}
            <Link to="/terms" className="text-primary hover:underline">Terms and Conditions</Link>
            {' '}and this Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
